import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "hooks/useAxios";
import { USER_API } from "config";
import { versionKeys } from "queries/version.keys";
import { projectKeys } from "queries/project.keys";

export type ProjectVersionSummary = {
  document_id: string;
  title: string;
  version: string;
  created_at: string;
};

export const useProjectVersions = (projectId?: number | string) => {
  const axios = useAxios();
  const pid = String(projectId ?? "");
  const enabled = pid.length > 0 && pid !== "0" && pid !== "undefined";
  return useQuery<ProjectVersionSummary[]>({
    queryKey: enabled ? versionKeys.list(pid) : (["versions", "disabled"] as const),
    enabled,
    queryFn: async () => {
      // Double guard to avoid accidental network calls with invalid IDs
      if (!enabled) return [] as ProjectVersionSummary[];
      const res = await axios.get(USER_API.getProjectVersions(pid));
      const data = res?.data ?? res;
      return Array.isArray(data) ? (data as ProjectVersionSummary[]) : [];
    },
    staleTime: 60_000,
  });
};

export const useActivateVersion = (projectId: number | string) => {
  const axios = useAxios();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: number | string) => {
      const response = await axios.patch(
        USER_API.activateVersion(String(projectId), String(documentId))
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate affected queries so UI reflects newly active version
      qc.invalidateQueries({ queryKey: versionKeys.list(String(projectId)) });
      qc.invalidateQueries({ queryKey: projectKeys.detail(String(projectId)) });
    },
  });
};

// Version settings DTOs based on FRONTEND_UPDATE_GUIDE.md
export type VersionSettings = {
  version_id: number;
  document_id: string;
  project_id: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export const useVersionSettings = (projectId?: number | string, documentId?: number | string) => {
  const axios = useAxios();
  const enabled = !!projectId && !!documentId;
  return useQuery<VersionSettings>({
    queryKey: enabled
      ? versionKeys.settings(String(projectId), String(documentId))
      : (["version-settings", "disabled"] as const),
    enabled,
    queryFn: async () => {
      const res = await axios.get(
        USER_API.getVersionSettings(String(projectId!), String(documentId!))
      );
      const data = res?.data ?? res;
      return data as VersionSettings;
    },
    staleTime: 60_000,
  });
};

export const useUpdateVersionSettings = (
  projectId: number | string,
  documentId: number | string
) => {
  const axios = useAxios();
  const qc = useQueryClient();
  return useMutation<VersionSettings, unknown, Record<string, unknown>>({
    mutationFn: async (settings: Record<string, unknown>) => {
      const res = await axios.patch(
        USER_API.patchVersionSettings(String(projectId), String(documentId)),
        settings
      );
      const data = res?.data ?? res;
      return data as VersionSettings;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: versionKeys.settings(String(projectId), String(documentId)),
      });
    },
  });
};
